import { fromJS } from "immutable";
import AppConstants from "@/constants/AppConstants";
import {
  BACKDROP_FILTER,
  RECTANGLE_BACKGROUND_COLOR,
} from "@/constants/MergeConstants";
import { FILL_SELECTION_ADVANCED } from "@/constants/SyncConstants";
import {
  ADVANCED_FILL_DEFAULT_BACKDROP_FILTER,
  ADVANCED_FILL_MODE_PRESETS,
} from "@/constants/AdvancedFillModePresetConstants";
import { formatPropUpdate } from "@/utils/PropertyUtils";
import { getAdvancedFillTransitionUpdates, getFillTypeTransitionUpdates } from "@/utils/Element/FillTypeTransitions";

const { UPDATE_ACTION_SET, UPDATE_ACTION_DELETE } = AppConstants;

/**
 * Make sure each BACKDROP_FILTER entry in sync-toggle has an action (set or delete)
 * @param {Array<{ name: string, value?: *, [key: string]: * }>} syncUpdates - Array of props to be synced
 * @returns {Array<Object>}
 */
export const normalizeToggleFillTypeSyncUpdates = (syncUpdates) => {
  if (!Array.isArray(syncUpdates)) return [];
  return syncUpdates.map((update) => {
    if (update.name && update.name.includes(BACKDROP_FILTER)) {
      return {
        ...update,
        action: update.value ? UPDATE_ACTION_SET : UPDATE_ACTION_DELETE,
      };
    }
    return update;
  });
}

/**
 * Returns the PropUpdate array for a fill type change.
 * @param {Object} propInfo - Property update with propInfo.value (new fill type)
 * @param {Object} params
 * @param {(path: string) => *} params.getValue
 * @param {(path: string) => boolean} params.hasProp
 * @param {(propName: string, parentProp?: string) => string} params.getPropName
 * @returns {Array<Object>}
 */
export const getFillTypeChangeUpdates = (propInfo, { getValue, hasProp, getPropName }) => {
  const newFillType = propInfo.value;

  if (newFillType === FILL_SELECTION_ADVANCED) {
    const transitionUpdates = getAdvancedFillTransitionUpdates({
      getValue,
      hasProp,
      getPropName,
    });
    return transitionUpdates.map(([propName, value, action]) =>
      formatPropUpdate(getPropName(propName), value, getValue(propName), action)
    );
  }

  const updates = [propInfo];
  const fillTypeTransitionUpdates = getFillTypeTransitionUpdates({ newFillType, getValue });
  updates.push(
    ...fillTypeTransitionUpdates.map(([propName, value, action]) =>
      formatPropUpdate(getPropName(propName), value, getValue(propName), action)
    )
  );
  return updates;
}

/**
 * Returns the PropUpdate array for applying an advanced fill preset.
 * @param {string} presetName - One of "darker" | "lighter" | "glassy" | "color-tint"
 * @param {Object} params
 * @param {(path: string) => *} params.getValue
 * @param {(propName: string, parentProp?: string) => string} params.getPropName
 * @returns {Array<Object>}
 */
export const getPresetSelectUpdates = (presetName, { getValue, getPropName }) => {
  const preset = ADVANCED_FILL_MODE_PRESETS[presetName];
  if (!preset) return [];

  return [
    formatPropUpdate(
      getPropName(BACKDROP_FILTER),
      fromJS(preset.backdropFilter),
      getValue(BACKDROP_FILTER),
      UPDATE_ACTION_SET,
      true
    ),
    formatPropUpdate(
      getPropName(RECTANGLE_BACKGROUND_COLOR),
      preset.color,
      getValue(RECTANGLE_BACKGROUND_COLOR),
      undefined,
      false
    ),
  ];
}

/**
 * Returns the PropUpdate array for changing one filter value inside the backdrop filter.
 * @param {Object} propInfo - Update with propInfo.name (e.g. "blur"), propInfo.value (new number)
 * @param {Object} params
 * @param {(path: string) => *} params.getValue
 * @param {(propName: string, parentProp?: string) => string} params.getPropName
 * @returns {Array<Object>}
 */
export const getBackdropFilterValueUpdates = (propInfo, { getValue, getPropName }) => {
  const filterFunctionName = propInfo.name;
  const newValue = propInfo.value;
  if (newValue === undefined || newValue === null) return [];

  const backdropFilter = getValue(BACKDROP_FILTER);
  const filterList = backdropFilter ?? fromJS(ADVANCED_FILL_DEFAULT_BACKDROP_FILTER);

  const idx = filterList.findIndex((item) => item?.get?.(filterFunctionName) !== undefined);
  const updatedBackdropFilter =
    idx === -1
      ? filterList
      : filterList.update(idx, (item) => item.set(filterFunctionName, Number(newValue)));

  return [
    {
      ...propInfo,
      name: getPropName(BACKDROP_FILTER),
      value: updatedBackdropFilter,
      origValue: backdropFilter,
      action: UPDATE_ACTION_SET,
    },
  ];
}
